// Yoga.jsx
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// === IMAGES === (replace with your actual high-quality assets)
import heroBg from '../assets/Mahi/139.png';
import yogaImportanceImg from '../assets/Mahi/140.png';
import yogaPracticeImg from '../assets/Mahi/141.png';


const fadeInUp = {
    hidden: { opacity: 0, y: 35 },
    visible: { opacity: 1, y: 0, transition: { duration: 1.1, ease: "easeOut" } },
};

const staggerContainer = {
    hidden: { opacity: 1 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.18, delayChildren: 0.2 },
    },
};

const childFade = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.9 } },
};

const Yoga = () => {
    const { t } = useTranslation('yoga');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const importancePoints = [
        t('importanceText1'),
        t('importanceText2'),
        t('importanceText3'),
    ];

    const practicePoints = [
        t('practiceText1'),
        t('practiceText2'),
        t('practiceText3'),
        t('practiceText4'),
    ];



    return (
        <div className="bg-[#F8F5ED] text-[#1A2F2A] min-h-screen font-sans">
            {/* ====================== HERO ====================== */}
            <section className="relative h-[70vh] min-h-[520px] flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src={heroBg}
                        alt={t('heroAlt')}
                        className="w-full h-full object-cover brightness-[0.78] scale-[1.03] transition-transform duration-[18s]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/55" />
                </div>

                <div className="relative z-10 text-center px-5 sm:px-8 max-w-5xl mx-auto">
                    <motion.h1
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.4, ease: "easeOut" }}
                        className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-serif font-light tracking-wide text-white leading-tight drop-shadow-2xl"
                    >
                        {t('heroTitle')}
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.4, delay: 0.45 }}
                        className="mt-6 text-xl sm:text-2xl md:text-3xl font-light text-[#E8C07D] tracking-wider"
                    >
                        {t('heroSubtitle')}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.92 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.5, delay: 0.8 }}
                        className="mt-12 inline-flex items-center gap-4"
                    >
                        <div className="h-px w-16 bg-[#E8C07D]/60" />
                        <span className="text-[#E8C07D] uppercase tracking-widest text-sm font-medium">
                            {t('importanceEyebrow')}
                        </span>
                        <div className="h-px w-16 bg-[#E8C07D]/60" />
                    </motion.div>
                </div>
            </section>

            {/* ====================== IMPORTANCE OF YOGA (sm only) ====================== */}
            <section className="py-16 sm:py-20 bg-gradient-to-b from-[#F8F5ED] to-[#F2EEE5] block md:hidden">
                <div className="container mx-auto px-5 sm:px-8">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-80px" }}
                        variants={staggerContainer}
                        className="text-center mb-10"
                    >
                        <motion.span
                            variants={childFade}
                            className="inline-block text-[#E8C07D] uppercase tracking-widest text-xs font-medium mb-3"
                        >
                            {t('importanceEyebrow')}
                        </motion.span>
                        <motion.h2
                            variants={childFade}
                            className="text-3xl sm:text-4xl font-serif font-light text-[#1A2F2A] leading-tight"
                        >
                            {t('importanceTitle')}
                        </motion.h2>
                        <motion.div
                            variants={childFade}
                            className="h-1 w-16 mx-auto mt-5 bg-gradient-to-r from-[#E8C07D] to-[#4A7C76] rounded-full"
                        />
                    </motion.div>

                    <div className="space-y-6">
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={fadeInUp}
                            className="relative rounded-3xl overflow-hidden shadow-2xl h-[320px] sm:h-[420px]"
                        >
                            <img
                                src={yogaImportanceImg}
                                alt={t('importanceImageAlt')}
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-70" />
                        </motion.div>

                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={staggerContainer}
                            className="space-y-4"
                        >
                            {importancePoints.map((point, i) => (
                                <motion.div
                                    key={i}
                                    variants={childFade}
                                    className="bg-white/70 backdrop-blur-sm p-6 rounded-2xl border-l-4 border-[#E8C07D]/60 hover:border-[#E8C07D] hover:shadow-xl transition-all duration-400"
                                >
                                    <p className="text-base sm:text-lg leading-relaxed text-[#2D3F3A]/90 font-light">
                                        {point}
                                    </p>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* ====================== IMPORTANCE OF YOGA ====================== */}
            <section className="py-20 md:py-28 lg:py-36 bg-gradient-to-b from-[#F8F5ED] to-[#F2EEE5] hidden md:block">
                <div className="container mx-auto px-5 sm:px-8 lg:px-12 ">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-80px" }}
                        variants={staggerContainer}
                        className="text-center mb-16 md:mb-20"
                    >
                        <motion.span
                            variants={childFade}
                            className="inline-block text-[#E8C07D] uppercase tracking-widest text-sm md:text-base font-medium mb-4"
                        >
                            {t('importanceEyebrow')}
                        </motion.span>
                        <motion.h2
                            variants={childFade}
                            className="text-4xl md:text-5xl lg:text-6xl font-serif font-light text-[#1A2F2A] leading-tight"
                        >
                            {t('importanceTitle')}
                        </motion.h2>
                        <motion.div
                            variants={childFade}
                            className="h-1 w-20 mx-auto mt-6 bg-gradient-to-r from-[#E8C07D] to-[#4A7C76] rounded-full"
                        />
                    </motion.div>

                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                        {/* Image */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={fadeInUp}
                            className="order-2 lg:order-1"
                        >
                            <div className="relative rounded-3xl overflow-hidden shadow-2xl h-[500px] lg:h-[600px] xl:h-[700px]">
                                <img
                                    src={yogaImportanceImg}
                                    alt={t('importanceImageAlt')}
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-70" />
                            </div>
                        </motion.div>

                        {/* Text points */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={staggerContainer}
                            className="space-y-7 order-1 lg:order-2"
                        >
                            {importancePoints.map((point, i) => (
                                <motion.div
                                    key={i}
                                    variants={childFade}
                                    className="bg-white/70 backdrop-blur-sm p-7 rounded-2xl border-l-4 border-[#E8C07D]/60 hover:border-[#E8C07D] hover:shadow-xl transition-all duration-400"
                                >
                                    <p className="text-lg md:text-xl leading-relaxed text-[#2D3F3A]/90 font-light">
                                        {point}
                                    </p>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* ====================== YOGA PRACTICE (sm only) ====================== */}
            <section className="py-16 sm:py-20 bg-[#F2EEE5] block md:hidden">
                <div className="container mx-auto px-5 sm:px-8">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={staggerContainer}
                        className="text-center mb-10"
                    >
                        <motion.span variants={childFade} className="text-[#E8C07D] uppercase tracking-widest text-xs font-medium">
                            {t('practiceEyebrow')}
                        </motion.span>
                        <motion.h2 variants={childFade} className="mt-3 text-3xl sm:text-4xl font-serif font-light text-[#1A2F2A]">
                            {t('practiceTitle')}
                        </motion.h2>
                        <motion.div variants={childFade} className="h-1 w-16 mx-auto mt-5 bg-gradient-to-r from-[#E8C07D] to-[#4A7C76] rounded-full" />
                    </motion.div>

                    <div className="space-y-6">
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={fadeInUp}
                            className="relative rounded-3xl overflow-hidden shadow-2xl h-[320px] sm:h-[420px]"
                        >
                            <img
                                src={yogaPracticeImg}
                                alt={t('practiceImageAlt')}
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-70" />
                        </motion.div>

                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={staggerContainer}
                            className="space-y-4"
                        >
                            {practicePoints.map((point, i) => (
                                <motion.div
                                    key={i}
                                    variants={childFade}
                                    className="bg-white/70 backdrop-blur-sm p-6 rounded-2xl border-l-4 border-[#E8C07D]/60 hover:border-[#E8C07D] hover:shadow-xl transition-all duration-400"
                                >
                                    <p className="text-base sm:text-lg leading-relaxed text-[#2D3F3A]/90 font-light">
                                        {point}
                                    </p>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* ====================== YOGA PRACTICE ====================== */}
            <section className="py-20 md:py-28 lg:py-36 bg-[#F2EEE5] hidden md:block">
                <div className="container mx-auto px-5 sm:px-8 lg:px-12 ">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={staggerContainer}
                        className="text-center mb-16 md:mb-20"
                    >
                        <motion.span variants={childFade} className="text-[#E8C07D] uppercase tracking-widest text-sm md:text-base font-medium">
                            {t('practiceEyebrow')}
                        </motion.span>
                        <motion.h2 variants={childFade} className="mt-4 text-4xl md:text-5xl lg:text-6xl font-serif font-light text-[#1A2F2A]">
                            {t('practiceTitle')}
                        </motion.h2>
                        <motion.div variants={childFade} className="h-1 w-20 mx-auto mt-6 bg-gradient-to-r from-[#E8C07D] to-[#4A7C76] rounded-full" />
                    </motion.div>

                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                        {/* Text points */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={staggerContainer}
                            className="space-y-7"
                        >
                            {practicePoints.map((point, i) => (
                                <motion.div
                                    key={i}
                                    variants={childFade}
                                    className="bg-white/70 backdrop-blur-sm p-7 rounded-2xl border-l-4 border-[#E8C07D]/60 hover:border-[#E8C07D] hover:shadow-xl transition-all duration-400"
                                >
                                    <p className="text-lg md:text-xl leading-relaxed text-[#2D3F3A]/90 font-light">
                                        {point}
                                    </p>
                                </motion.div>
                            ))}
                        </motion.div>

                        {/* Image */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={fadeInUp}
                            className="relative rounded-3xl overflow-hidden shadow-2xl h-[500px] lg:h-[600px] xl:h-[700px]"
                        >
                            <img
                                src={yogaPracticeImg}
                                alt={t('practiceImageAlt')}
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-70" />
                        </motion.div>
                    </div>
                </div>
            </section>


        </div>
    );
};

export default Yoga;